import React from 'react'
import { useOportunidadesRevisao } from '../hooks/useOportunidadesRevisao'
import type { OpportunityItem } from '../types'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { useToast } from '@/shared/ui/Toast'
import { rejectOpportunity } from '../services/oportunidadesService'
import { Eye, MoreHorizontal } from 'lucide-react'
import { useOportunidadeModal } from '../context/OportunidadeModalContext'
import { useReviewModal } from '../context/ReviewModalContext'

export default function OportunidadesReviewList({ onAfterPublish }: { onAfterPublish?: ()=>void }) {
  const { items, loading, error } = useOportunidadesRevisao()
  return (
    <div className="w-full">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-lg font-semibold">Em Revisão</div>
          <div className="text-xs text-[#434343]">Somente visível para o master. Publique para liberar aos demais.</div>
        </div>
      </div>
      {error && (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>
      )}
      {loading ? (
        <div className="text-sm text-[#434343]">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[#434343]">Nenhuma oportunidade em revisão no momento.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((it)=> (
            <Row key={it.id} item={it} />
          ))}
        </div>
      )}
    </div>
  )
}

function Row({ item }: { item: OpportunityItem }) {
  const { openModal, openDebug } = useOportunidadeModal()
  const { openReview } = useReviewModal()
  const { add: toast } = useToast()
  const [working, setWorking] = React.useState(false)

  async function handleReject() {
    if (working) return
    setWorking(true)
    try {
      await rejectOpportunity(item.id)
      toast({ title: 'Revisão', description: 'Oportunidade rejeitada.', variant: 'success' })
      try { window.dispatchEvent(new Event('opportunities:refresh')) } catch {}
    } catch (e: any) {
      toast({ title: 'Revisão', description: e?.message || 'Falha ao rejeitar', variant: 'error' })
    } finally {
      setWorking(false)
    }
  }
  return (
    <div className="rounded-xl border border-[#161616] bg-background/40 p-4">
      <div className="grid items-center gap-6 md:grid-cols-[1.2fr,0.9fr,1fr,0.8fr,0.6fr,1.4fr,0.4fr]">
        {/* Asset */}
        <div className="flex items-center gap-3">
          {item.logo_url ? (
            <img src={item.logo_url} alt={item.symbol} className="h-8 w-8 flex-shrink-0 rounded-full object-cover" />
          ) : (
            <div className="h-8 w-8 flex-shrink-0 rounded-full bg-muted" />
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{item.name || item.symbol}</div>
            <div className="truncate text-xs text-[#434343]">{item.symbol}</div>
          </div>
        </div>
        {/* Timeframes */}
        <div>
          <div className="text-sm font-medium">{[item.fractalLower, item.fractalUpper].filter(Boolean).join(' | ') || '-'}</div>
        </div>
        {/* Recommendation */}
        <div>
          <div className="text-sm font-medium">{item.recommendation || '-'}</div>
        </div>
        {/* Objective */}
        <div>
          <div className={[
            'text-sm font-medium',
            item.objective === 'Long' ? 'text-emerald-400' : item.objective === 'Short' ? 'text-red-400' : 'text-foreground/80',
          ].join(' ')}>
            {item.objective === 'Long' ? 'bullish' : item.objective === 'Short' ? 'bearish' : '-'}
          </div>
        </div>
        {/* Status placeholder */}
        <div />
        {/* Confluence text (compact) */}
        <div className="pr-2 md:pr-4 min-w-0">
          {Array.isArray(item.confluences) && item.confluences.length > 0 && (
            <div className="truncate text-xs text-foreground/80">{item.confluences.join(', ')}</div>
          )}
        </div>
        {/* Action */}
        <div className="flex items-center justify-end">
          <DropdownMenu
            trigger={<button type="button" className="inline-flex items-center rounded p-1 hover:bg-muted/30"><MoreHorizontal className="h-5 w-5 text-[#434343]" /></button>}
            items={[
              { label: 'Revisar', onSelect: () => openReview({ id: item.id, symbol: item.symbol, name: item.name, logo_url: item.logo_url }) },
              { label: 'Detalhes', onSelect: () => openModal({ id: item.id, symbol: item.symbol, name: item.name, logo_url: item.logo_url, kind: 'opportunity' }) },
              { label: 'Debug', onSelect: () => openDebug({ id: item.id, symbol: item.symbol, name: item.name, logo_url: item.logo_url, kind: 'opportunity' }) },
              { label: working ? 'Rejeitando...' : 'Rejeitar', onSelect: () => handleReject() },
            ]}
            align="end"
          />
        </div>
      </div>
    </div>
  )
}
